/**
 * 等级评估规则 - 前端自定义 Widget 共享常量
 *
 * 与后端枚举保持一致：
 * - 指标名称：DeliAriesEvaluationIndicatorNameEnum
 * - 运算符：DeliAriesIndicatorRange.operator（GE/GT/LE/LT/EQ/BRACKET/BRACKET_LE）
 * - 星级：DeliAriesStarRule.starLevel（≥1 的整数，不固定档数）
 */

/** 指标名称枚举选项（value 与后端枚举 value 一致） */
export const INDICATOR_NAME_OPTIONS: { label: string; value: string }[] = [
  { label: '合作系统（家数）', value: 'COOPERATION_SYSTEM_COUNT' },
  { label: '得力品牌坑位数', value: 'DELI_BRAND_SLOT_COUNT' },
  { label: '中心仓主要分布省份（个数）', value: 'CENTRAL_WAREHOUSE_PROVINCE_COUNT' },
  { label: '现有生意规模（万）', value: 'CURRENT_BUSINESS_SCALE' },
  { label: '店月均订单数', value: 'MONTHLY_AVERAGE_ORDERS' },
  { label: '系统门店数（家）', value: 'SYSTEM_STORE_COUNT' },
  { label: '团队人数', value: 'TEAM_SIZE' },
  { label: '运营人数', value: 'OPERATION_STAFF_COUNT' },
  { label: '合作系统名称', value: 'COOPERATION_SYSTEM_NAME' },
  { label: '主营品牌', value: 'MAIN_BRAND' },
  { label: '竞品代理情况', value: 'COMPETITOR_AGENT_SITUATION' },
  { label: '学习办公用品年销（万）', value: 'LEARNING_OFFICE_ANNUAL_SALES' },
  { label: '客户主营业务', value: 'CUSTOMER_MAIN_BUSINESS' },
  { label: '系统全年GMV（万）', value: 'SYSTEM_ANNUAL_GMV' },
  { label: '学习办公用品GMV（万）', value: 'LEARNING_OFFICE_GMV' },
  { label: '得力品牌GMV（万）', value: 'DELI_BRAND_GMV' }
];

/** 指标名称 value → label 映射 */
export const INDICATOR_NAME_LABEL_MAP: Record<string, string> = INDICATOR_NAME_OPTIONS.reduce(
  (acc, cur) => {
    acc[cur.value] = cur.label;
    return acc;
  },
  {} as Record<string, string>
);

/**
 * 适用渠道 → 可选指标映射（value 与后端 DeliAriesEvaluationChannelEnum 一致）。
 * 供应链：合作系统（家数）、得力品牌坑位数、中心仓主要分布省份、现有生意规模、
 *         合作系统名称、主营品牌、竞品代理情况、学习办公用品年销、团队人数、运营人数、客户主营业务
 * 闪电仓：系统门店数（家）、得力品牌坑位数、中心仓主要分布省份、店月均订单数、
 *         系统全年GMV、学习办公用品GMV、得力品牌GMV、团队人数、运营人数
 */
export const CHANNEL_INDICATOR_OPTIONS: Record<string, { label: string; value: string }[]> = {
  SUPPLY_CHAIN: [
    { label: '合作系统（家数）', value: 'COOPERATION_SYSTEM_COUNT' },
    { label: '得力品牌坑位数', value: 'DELI_BRAND_SLOT_COUNT' },
    { label: '中心仓主要分布省份（个数）', value: 'CENTRAL_WAREHOUSE_PROVINCE_COUNT' },
    { label: '现有生意规模（万）', value: 'CURRENT_BUSINESS_SCALE' },
    { label: '合作系统名称', value: 'COOPERATION_SYSTEM_NAME' },
    { label: '主营品牌', value: 'MAIN_BRAND' },
    { label: '竞品代理情况', value: 'COMPETITOR_AGENT_SITUATION' },
    { label: '学习办公用品年销（万）', value: 'LEARNING_OFFICE_ANNUAL_SALES' },
    { label: '团队人数', value: 'TEAM_SIZE' },
    { label: '运营人数', value: 'OPERATION_STAFF_COUNT' },
    { label: '客户主营业务', value: 'CUSTOMER_MAIN_BUSINESS' }
  ],
  LIGHTNING: [
    { label: '系统门店数（家）', value: 'SYSTEM_STORE_COUNT' },
    { label: '得力品牌坑位数', value: 'DELI_BRAND_SLOT_COUNT' },
    { label: '中心仓主要分布省份（个数）', value: 'CENTRAL_WAREHOUSE_PROVINCE_COUNT' },
    { label: '店月均订单数', value: 'MONTHLY_AVERAGE_ORDERS' },
    { label: '系统全年GMV（万）', value: 'SYSTEM_ANNUAL_GMV' },
    { label: '学习办公用品GMV（万）', value: 'LEARNING_OFFICE_GMV' },
    { label: '得力品牌GMV（万）', value: 'DELI_BRAND_GMV' },
    { label: '团队人数', value: 'TEAM_SIZE' },
    { label: '运营人数', value: 'OPERATION_STAFF_COUNT' }
  ]
};

/** 运算符枚举值 */
export enum RangeOperator {
  GE = 'GE',
  GT = 'GT',
  LE = 'LE',
  LT = 'LT',
  EQ = 'EQ',
  BRACKET = 'BRACKET',
  BRACKET_LE = 'BRACKET_LE'
}

/** 运算符下拉选项 */
export const OPERATOR_OPTIONS: { label: string; value: RangeOperator }[] = [
  { label: '≥', value: RangeOperator.GE },
  { label: '>', value: RangeOperator.GT },
  { label: '≤', value: RangeOperator.LE },
  { label: '<', value: RangeOperator.LT },
  { label: '=', value: RangeOperator.EQ },
  { label: '[a,b)', value: RangeOperator.BRACKET },
  { label: '(a,b]', value: RangeOperator.BRACKET_LE }
];

/** 运算符 value → 符号 映射 */
export const OPERATOR_SYMBOL_MAP: Record<string, string> = {
  GE: '≥',
  GT: '>',
  LE: '≤',
  LT: '<',
  EQ: '=',
  BRACKET: '[a,b)',
  BRACKET_LE: '(a,b]'
};

/** 是否为区间（双值）运算符 */
export function isBracket(operator?: string): boolean {
  return operator === RangeOperator.BRACKET || operator === RangeOperator.BRACKET_LE;
}

/** 单个区间明细结构 */
export interface IIndicatorRange {
  operator: string;
  minValue: number | null;
  maxValue: number | null;
  score: number | null;
}

/** 单个指标结构（含区间明细） */
export interface IIndicator {
  indicatorName: string | null;
  ranges: IIndicatorRange[];
}

/** 星级规则结构 */
export interface IStarRule {
  starLevel: number | null;
  minScore: number | null;
  maxScore: number | null;
  description: string;
}

/** 创建页默认星级规则：1 行 1 星，分数区间/描述为空 */
export function buildCreateDefaultStarRules(): IStarRule[] {
  return [{ starLevel: 1, minScore: null, maxScore: null, description: '' }];
}

/** 生成默认区间明细（一条 ≥ 区间） */
export function buildDefaultRange(): IIndicatorRange {
  return { operator: RangeOperator.GE, minValue: null, maxValue: null, score: null };
}

/** 生成默认指标 */
export function buildDefaultIndicator(): IIndicator {
  return { indicatorName: null, ranges: [buildDefaultRange()] };
}

/**
 * 归一化枚举值：兼容后端可能返回字符串或 {value,displayName} 对象的情况
 */
export function normalizeEnumValue(raw: unknown): string | null {
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'string') {
    return raw;
  }
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (typeof obj.value === 'string') {
      return obj.value;
    }
    if (typeof obj.name === 'string') {
      return obj.name;
    }
  }
  return String(raw);
}

/** 星级数字 → 展示文本（≥1 整数，如 6 → "6星"） */
export function starLevelText(lv: number | null | undefined): string {
  return lv != null && Number.isInteger(lv) && lv >= 1 ? `${lv}星` : '-';
}

/** 格式化单个星级规则的分数区间文本：[min,max) / ≥min / <max，两者皆空返回空串 */
export function formatStarRange(rule: { minScore: number | null; maxScore: number | null }): string {
  const hasMin = rule.minScore != null;
  const hasMax = rule.maxScore != null;
  if (hasMin && hasMax) {
    return `[${rule.minScore},${rule.maxScore})`;
  }
  if (hasMin) {
    return `≥${rule.minScore}`;
  }
  if (hasMax) {
    return `<${rule.maxScore}`;
  }
  return '';
}

/**
 * 格式化星级规则摘要（列表页用），如：
 * "五星[90,120) 三星[70,90) 二星[50,70) 一星<50"（高星级如 6星 展示为 "6星[...]"）
 * 仅展示已配置区间的档位，按星级从高到低排序。
 */
export function formatStarRuleSummary(list: any[]): string {
  if (!Array.isArray(list) || !list.length) {
    return '';
  }
  return list
    .map((r) => ({
      starLevel: Number(r?.starLevel),
      minScore: r?.minScore ?? null,
      maxScore: r?.maxScore ?? null
    }))
    .filter((r) => Number.isInteger(r.starLevel) && r.starLevel >= 1)
    .sort((a, b) => b.starLevel - a.starLevel)
    .map((r) => {
      const range = formatStarRange(r);
      return range ? `${r.starLevel}星${range}` : '';
    })
    .filter((s) => s)
    .join(' ');
}

/** 格式化单个区间为文本，如 ">=500"、"[300,500)"、"<50" */
export function formatRange(range: IIndicatorRange): string {
  const op = range.operator;
  const min = range.minValue ?? '';
  const max = range.maxValue ?? '';
  switch (op) {
    case RangeOperator.GE:
      return `≥${min}`;
    case RangeOperator.GT:
      return `>${min}`;
    case RangeOperator.LE:
      return `≤${min}`;
    case RangeOperator.LT:
      return `<${min}`;
    case RangeOperator.EQ:
      return `=${min}`;
    case RangeOperator.BRACKET:
      return `[${min},${max})`;
    case RangeOperator.BRACKET_LE:
      return `(${min},${max}]`;
    default:
      return `${min}`;
  }
}
